import {
  Checkbox,
  Table as FluentUITable,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tag,
  TagGroup,
} from '@fluentui/react-components';
import { ArrowDown20Regular, ArrowUp20Regular, Filter24Regular } from '@fluentui/react-icons';
import React, { ReactNode, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button/Button';
import { Popup } from '../Popup/Popup';
import { Text } from '../Text/Text';
import {
  applyFilter,
  applySort,
  dismissFilterPill,
  resetFilter,
  setColumn,
  setData,
  setSelection,
  toggleAll,
  toggleItem,
} from './Table.action';
import Filter from './Table.filter';
import { ApplyFilterPayloadProps, TableProps } from './Table.interface';
import styles from './Table.module.css';
import {
  getColumns,
  getData,
  getFilterValues,
  getSelectedItems,
  getSortData,
  initialState,
  reducer,
} from './Table.reducer';
import { isNotEqual } from './Table.utils';

// sort column
const sortColumnHeader = (
  sort: boolean,
  content: string | ReactNode,
  order: number,
  onOrderChange: (order: number) => void,
  cellStyles: React.CSSProperties = {},
) => ({
  content: (
    <div className="flex gap-2 items-center">
      <Text weight="bold">{content}</Text>
      <>
        {order !== 0 && sort ? order === 1 ? <ArrowUp20Regular /> : <ArrowDown20Regular /> : null}
      </>
    </div>
  ),
  key: content,
  onClick: () => onOrderChange(order === 0 ? 1 : -order),
  'aria-sort': order !== 0 ? (order > 0 ? 'ascending' : 'descending') : undefined,
  styles: cellStyles, // setting dynamic width for the column cell
  title: content,
});

// toggle method
const selectColumn = (select: boolean, checked: boolean, callback: (data: boolean) => void) => {
  if (!select) return;
  return {
    content: (
      <Checkbox
        checked={checked}
        onChange={(_, data) => {
          callback(data.checked === true);
        }}
        aria-label="select"
      />
    ),
    key: 'checkbox',
    styles: { width: '40px', maxWidth: '40px' } as React.CSSProperties,
  };
};

// NOTE:
// usePrevious function - storing previous value
// useful to check with previous and current values
// this method is used instead of maintaining local
// state in table component itself
const usePrevious = <T extends unknown>(value: T): T | undefined => {
  const reference = React.useRef<T | undefined>(undefined);
  useEffect(() => {
    reference.current = value;
  });
  return reference.current;
};

const Table: React.FC<TableProps> = ({
  columns,
  data,
  selectedFilter = {},
  tableHeader,
  select = false,
  onSelectedItems,
  selectedItems = [],
  lazyThreshold = 0,
  className = '',
  options = {
    showFilterPopup: true,
  },
}) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    selectedItems,
  });
  const selectedItemsSelector = getSelectedItems(state);
  const [open, setOpen] = useState<boolean>(false);
  const previousSelectedItems = usePrevious(selectedItemsSelector);
  const [limit, setLimit] = useState<number>(1);

  useEffect(() => {
    dispatch(setColumn({ columns }));
    dispatch(setData({ data }));
  }, [data, columns]);

  useEffect(() => {
    if (isNotEqual(selectedItems, previousSelectedItems ?? [])) {
      dispatch(setSelection(selectedItems));
    }
  }, [selectedItems, previousSelectedItems]);

  useEffect(() => {
    if (!select) return;
    // NOTE:
    // onClick of the checkbox this callback will trigger in the parent component
    // which passes the selectedItems to it, comparing previous selectedItems
    // should not be equal to next selectedItems
    if (
      onSelectedItems &&
      previousSelectedItems &&
      isNotEqual(selectedItemsSelector, previousSelectedItems)
    ) {
      onSelectedItems(selectedItemsSelector);
    }
  }, [select, selectedItemsSelector, onSelectedItems, previousSelectedItems]);

  const { columns: headers, headerColumns } = getColumns(state);
  const { allRows, data: rows } = getData(state);
  const { filterKeys, filterValues } = getFilterValues(state);
  const { sortColumn, sortDirection } = getSortData(state);
  const headerItems = [
    selectColumn(
      select,
      rows.length > 0 && selectedItemsSelector.length === rows.length,
      (callback) => {
        dispatch(toggleAll({ checked: callback }));
      },
    ),
    ...headerColumns.map((c) =>
      sortColumnHeader(
        c.sort,
        c.content,
        c.uniqueKey === sortColumn ? sortDirection : 0,
        (newSortDirection) => {
          dispatch(
            applySort({
              sortColumn: c.uniqueKey,
              sortDirection: newSortDirection,
            }),
          );
        },
        c.styles,
      ),
    ),
  ].filter(Boolean);
  const { t } = useTranslation();

  const endLimit = () =>
    lazyThreshold >= rows.length || lazyThreshold === 0 ? rows.length : lazyThreshold * limit;

  const tableRows = rows
    .map((element) => ({
      ...element,
      items: [
        selectColumn(select, selectedItemsSelector.includes(element.key.toString()), (callback) => {
          dispatch(
            toggleItem({
              itemKey: element.key.toString(),
              checked: callback,
            }),
          );
        }),
        ...element.items.map((item) => item),
      ].filter(Boolean),
    }))
    .slice(0, endLimit());

  useEffect(() => {
    if (Object.keys(selectedFilter).length > 0) {
      dispatch(applyFilter(selectedFilter));
    }
  }, [selectedFilter]);

  const handleFilterPopup = () => setOpen((previousState) => !previousState);

  const onTableFilterValues = (values: ApplyFilterPayloadProps) => dispatch(applyFilter(values));

  const handleReset = () => dispatch(resetFilter());

  const showMore = () => setLimit((previousState) => previousState + 1);

  return (
    <div className={`${styles.Table} ${className}`}>
      <div className={`${styles.tableHeaderFlex} flex gap-2 justify-between`}>
        <div className={styles.leftColumn}>
          {filterKeys.length > 0 ? (
            <TagGroup onDismiss={(_, { value }) => dispatch(dismissFilterPill(value))}>
              {filterKeys.map((key) => (
                <Tag
                  key={key}
                  // aix-tag-grey: Fluent fills a tag with the same color as this table's
                  // surface (--page-bg-color), which would make the pill invisible.
                  className={`aix-tag-grey ${key}`}
                  value={key}
                  dismissible
                  dismissIcon={{
                    'aria-label': t('tableFilter_remove', { defaultValue: 'Filter entfernen' }),
                  }}
                >
                  {filterValues[key]}
                </Tag>
              ))}
            </TagGroup>
          ) : (
            tableHeader
          )}
        </div>

        {options.showFilterPopup && (
          <div>
            <Popup
              open={open}
              align="end"
              pointing
              position="below"
              trigger={
                <Button
                  aria-label={t('table_filter_aria_label', {
                    defaultValue: 'Öffnet ein Popup mit Filteroptionen',
                  })}
                  shape="circular"
                  icon={<Filter24Regular />}
                  name="filter"
                  title="filter"
                />
              }
              content={
                <Filter
                  columns={headers}
                  data={allRows}
                  onClose={handleFilterPopup}
                  onTableFilterValues={onTableFilterValues}
                  filterValues={filterValues}
                  reset={handleReset}
                />
              }
              onOpenChange={handleFilterPopup}
            />
          </div>
        )}
      </div>
      <FluentUITable className={styles.fluentUITable}>
        <TableHeader>
          <TableRow className={styles.headerSection}>
            {headerItems.map((item) => (
              <TableHeaderCell
                key={String(item?.key)}
                onClick={item && 'onClick' in item ? item.onClick : undefined}
                aria-sort={
                  item && 'aria-sort' in item
                    ? (item['aria-sort'] as 'ascending' | 'descending' | undefined)
                    : undefined
                }
                title={item && 'title' in item ? (item.title as string) : undefined}
                style={item?.styles}
              >
                {item?.content}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableRows.map((row) => (
            <TableRow key={row.key}>
              {row.items.map((item, index) => (
                <TableCell
                  key={`${row.key}-${item?.key ?? index}`}
                  style={item?.styles}
                  title={item && 'title' in item ? (item.title as string) : undefined}
                >
                  {item?.content}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </FluentUITable>
      {rows.length !== tableRows.length && (
        <Button
          className="w-full"
          aria-label={t('table_showMore_aria_label', {
            defaultValue: 'Zeigt weitere Elemente am unteren Rand der Tabelle an',
          })}
          onClick={showMore}
          title={t('table_showMore', { defaultValue: 'Mehr anzeigen' })}
        >
          {t('table_showMore', { defaultValue: 'Mehr anzeigen' })}
        </Button>
      )}
    </div>
  );
};

export { Table };
